import React, { useRef, useState } from "react";
import { adminFetch } from "./auth";
import { resolveSrc } from "./editorControls";
import { membershipDocuments as ORIGINALS } from "../data/redlandGolfContent";

const SLOTS = [
  { key: "information", title: "Membership Information", ext: "pdf", label: "PDF" },
  { key: "agreement", title: "Membership Agreement", ext: "docx", label: "Word (.docx)", printable: true },
  { key: "application", title: "Membership Application", ext: "xlsx", label: "Excel (.xlsx)" },
];

const hasExt = (name, ext) => (name || "").trim().toLowerCase().endsWith(`.${ext}`);

export function validateDocuments(value) {
  const errs = [];
  SLOTS.forEach((s) => {
    const d = value[s.key] || {};
    if (!d.url) errs.push(`${s.title}: a file is required.`);
    if (!hasExt(d.fileName, s.ext)) errs.push(`${s.title}: the displayed file name must end in .${s.ext}`);
    if (s.printable && d.pdfUrl && !hasExt(d.pdfFileName, "pdf")) errs.push(`${s.title}: the printable PDF name must end in .pdf`);
  });
  return errs;
}

function UploadButton({ ext, label, testid, onUploaded, onStatus }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (ref.current) ref.current.value = "";
    if (!f) return;
    if (!hasExt(f.name, ext)) {
      onStatus("error", `Please choose a .${ext} file.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      onUploaded(await adminFetch("/api/admin/upload-document", { method: "POST", body: fd }));
    } catch (err) {
      onStatus("error", err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={`.${ext}`} style={{ display: "none" }} onChange={onFile} data-testid={`${testid}-file`} />
      <button className="admin-mini" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testid}-upload`}>
        {busy ? "Uploading…" : label}
      </button>
    </>
  );
}

function CurrentFile({ url, name, testid }) {
  const isOriginal = url && !url.startsWith("/api/");
  return (
    <p className="admin-hint" data-testid={`${testid}-current`}>
      Current file:{" "}
      <a href={resolveSrc(url)} target="_blank" rel="noreferrer" data-testid={`${testid}-open`}>{name || "Open file"}</a>
      {isOriginal ? " (original)" : " (uploaded)"}
    </p>
  );
}

function PrintablePdf({ doc, set, onStatus }) {
  return (
    <div className="admin-subblock">
      <h3>Printable PDF (optional)</h3>
      {doc.pdfUrl ? (
        <>
          <CurrentFile url={doc.pdfUrl} name={doc.pdfFileName} testid="doc-agreement-pdf" />
          <label>Displayed PDF file name
            <input value={doc.pdfFileName || ""} onChange={(e) => set({ pdfFileName: e.target.value })} data-testid="doc-agreement-pdf-name" />
          </label>
        </>
      ) : (
        <p className="admin-hint" data-testid="doc-agreement-pdf-hidden">No printable PDF — the "Printable PDF" button is hidden on the website.</p>
      )}
      <div className="admin-row">
        <UploadButton
          ext="pdf"
          label={doc.pdfUrl ? "Replace Printable PDF" : "Upload Printable PDF"}
          testid="doc-agreement-pdf"
          onStatus={onStatus}
          onUploaded={(r) => { set({ pdfUrl: r.url, pdfFileName: r.fileName }); onStatus("success", "Printable PDF uploaded. Save Draft or Publish to apply."); }}
        />
        {doc.pdfUrl ? (
          <button className="admin-mini danger" onClick={() => set({ pdfUrl: "", pdfFileName: "" })} data-testid="doc-agreement-pdf-remove">Remove Printable PDF</button>
        ) : null}
      </div>
    </div>
  );
}

function DocSlot({ slot, doc, onChange, onStatus }) {
  const set = (patch) => onChange({ ...doc, ...patch });
  const original = ORIGINALS[slot.key];
  const isOriginal = JSON.stringify(doc) === JSON.stringify(original);
  const t = `doc-${slot.key}`;
  const onUploaded = (r) => {
    if (slot.printable) {
      set({ url: r.url, fileName: r.fileName, pdfUrl: "", pdfFileName: "" });
      onStatus("success", "Agreement uploaded. The Printable PDF button is now hidden — upload a matching PDF below if you have one.");
    } else {
      set({ url: r.url, fileName: r.fileName });
      onStatus("success", `${slot.title} uploaded. Save Draft or Publish to apply.`);
    }
  };
  const restore = () => {
    if (window.confirm(`Restore the original ${slot.title} file? This replaces the current file in your draft.`)) onChange({ ...original });
  };
  return (
    <div className="admin-card-editor" data-testid={`${t}-editor`}>
      <h3>{slot.title} <span className="admin-hint">({slot.label})</span></h3>
      <CurrentFile url={doc.url} name={doc.fileName} testid={t} />
      <label>Displayed file name
        <input value={doc.fileName || ""} onChange={(e) => set({ fileName: e.target.value })} data-testid={`${t}-name`} />
      </label>
      <div className="admin-row">
        <UploadButton ext={slot.ext} label={`Replace File (.${slot.ext})`} testid={t} onUploaded={onUploaded} onStatus={onStatus} />
        <button className="admin-mini" disabled={isOriginal} onClick={restore} data-testid={`${t}-restore`}>Restore Original</button>
      </div>
      {slot.printable ? <PrintablePdf doc={doc} set={set} onStatus={onStatus} /> : null}
    </div>
  );
}

export function DocumentsEditor({ value, onChange, onStatus }) {
  return (
    <section className="admin-section" data-testid="documents-editor">
      <h2>Membership Documents</h2>
      <p className="admin-hint">
        Replace the files shown in the Membership Documents section of the Memberships page. New files stay private until you click Publish.
      </p>
      {SLOTS.map((slot) => (
        <DocSlot key={slot.key} slot={slot} doc={value[slot.key] || {}} onChange={(d) => onChange({ ...value, [slot.key]: d })} onStatus={onStatus} />
      ))}
    </section>
  );
}
